import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Shield, Loader2, Monitor, Smartphone, LogOut, RefreshCw, Globe, Clock, AlertTriangle } from 'lucide-react';
import api from '@/services/api';

const detectDeviceIcon = (ua = '') => {
  const s = ua.toLowerCase();
  if (/iphone|ipad|android|mobile/.test(s)) return Smartphone;
  return Monitor;
};

const prettyDevice = (ua = '') => {
  if (!ua || ua === 'unknown') return 'Unknown device';
  const browser = /chrome\/[\d.]+/.test(ua) ? 'Chrome'
    : /firefox\/[\d.]+/.test(ua) ? 'Firefox'
    : /safari\/[\d.]+/.test(ua) && !/chrome/.test(ua) ? 'Safari'
    : /edg\/[\d.]+/.test(ua) ? 'Edge'
    : /opera|opr\//.test(ua) ? 'Opera'
    : 'Browser';
  const os = /windows nt/.test(ua) ? 'Windows'
    : /mac os/.test(ua) ? 'macOS'
    : /android/.test(ua) ? 'Android'
    : /iphone|ipad/.test(ua) ? 'iOS'
    : /linux/.test(ua) ? 'Linux'
    : 'Unknown OS';
  return `${browser} on ${os}`;
};

const formatTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleString();
};

export const SecuritySessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/sessions');
      setSessions(res.data.sessions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRevokeOthers = async () => {
    const others = sessions.filter((s) => !s.is_current).length;
    if (others === 0) { toast.info('No other devices to sign out'); return; }
    if (!window.confirm(`Sign out of ${others} other ${others === 1 ? 'device' : 'devices'}? They will need to log in again.`)) return;
    setRevoking(true);
    try {
      const res = await api.post('/auth/sessions/revoke-others');
      toast.success(`Signed out of ${res.data.revoked_count} ${res.data.revoked_count === 1 ? 'device' : 'devices'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to sign out other devices');
    } finally {
      setRevoking(false);
    }
  };

  const handleRevokeOne = async (sessionId) => {
    if (!window.confirm('Sign out of this device?')) return;
    setRevokingId(sessionId);
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      toast.success('Device signed out');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to sign out device');
    } finally {
      setRevokingId(null);
    }
  };

  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="security-sessions-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Active Sessions & Devices
          </CardTitle>
          <CardDescription className="text-slate-600">
            Devices currently signed in to your account. Sign out of any you don&apos;t recognize.
          </CardDescription>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sessions-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleRevokeOthers}
            disabled={revoking || otherCount === 0}
            className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
            data-testid="sessions-revoke-others-btn"
          >
            {revoking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5 mr-1.5" />}
            Sign out other devices ({otherCount})
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading sessions…
          </div>
        ) : sessions.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm">No active sessions.</div>
        ) : (
          <div className="space-y-2" data-testid="sessions-list">
            {sessions.map((s) => {
              const Icon = detectDeviceIcon(s.user_agent || '');
              return (
                <div
                  key={s.session_id}
                  className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${
                    s.is_current
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`session-row-${s.is_current ? 'current' : s.session_id}`}
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      s.is_current ? 'bg-emerald-100' : 'bg-slate-100'
                    }`}>
                      <Icon className={`w-4 h-4 ${s.is_current ? 'text-emerald-700' : 'text-slate-600'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 text-sm">{prettyDevice(s.user_agent)}</span>
                        {s.is_current && (
                          <Badge className="bg-emerald-600 text-white text-[10px] tracking-wide">THIS DEVICE</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                        {s.ip_address && s.ip_address !== 'unknown' && (
                          <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {s.ip_address}</span>
                        )}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Last active {formatTime(s.last_seen_at)}</span>
                      </div>
                    </div>
                  </div>
                  {!s.is_current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeOne(s.session_id)}
                      disabled={revokingId === s.session_id}
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 shrink-0"
                      data-testid={`session-revoke-${s.session_id}`}
                    >
                      {revokingId === s.session_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {otherCount > 0 && (
          <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>Don&apos;t recognize a device? Sign it out immediately and change your password.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SecuritySessions;
